import {
  getDefaultBranding,
  updateBrandingDom,
  adjustColorBrightness,
  hexToRgb,
} from '../utils/brandConfig';
import { SettingsService } from '../api/services/settingsService';
import { HotelService } from '../api/services/hotelService';
import { getCookie } from '../utils/cookies';

/**
 * Synchronous initial branding state reader.
 * Uses cached tenant branding if available to eliminate color flicker on boot/refresh.
 * If not cached, initializes with neutral state (NO hardcoded green, NO dummy colors).
 */
export const getInitialBrandingState = () => {
  let activeTenantId = null;
  const hasToken =
    typeof document !== 'undefined' && document.cookie.indexOf('adminToken=') !== -1;

  if (typeof window !== 'undefined' && hasToken) {
    try {
      const authData = JSON.parse(
        getCookie('adminAuth') || localStorage.getItem('adminAuth') || '{}'
      );
      activeTenantId = authData?.tenantId;
    } catch {}
  }

  // 1. If authenticated and active tenant exists, check tenant branding cache
  if (
    hasToken &&
    activeTenantId &&
    activeTenantId !== 'null' &&
    activeTenantId !== 'undefined'
  ) {
    try {
      const cached = localStorage.getItem(`app_tenant_branding_${activeTenantId}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && (parsed.primaryColor || parsed.logoUrl || parsed.appName)) {
          updateBrandingDom(parsed);
          return {
            ...parsed,
            isCustomLoaded: true,
            isLoading: false,
            error: null,
          };
        }
      }
    } catch {}
  } else {
    // 2. Unauthenticated / Login page: check public branding cache
    try {
      const cached = localStorage.getItem('app_public_branding');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && (parsed.primaryColor || parsed.logoUrl || parsed.appName)) {
          updateBrandingDom(parsed);
          return {
            ...parsed,
            isCustomLoaded: true,
            isLoading: false,
            error: null,
          };
        }
      }
    } catch {}
  }

  // 3. Fallback uninitialized state (clean neutral values, no green, no dummy colors)
  const defaults = getDefaultBranding();
  return {
    ...defaults,
    primaryColor: '',
    secondaryColor: '',
    accentColor: '',
    logo: null,
    logoUrl: null,
    logoBlue: null,
    favicon: null,
    faviconUrl: null,
    isCustomLoaded: false,
    isLoading: true,
    error: null,
  };
};

const initialBranding = getInitialBrandingState();

// Deduplication locks for in-flight API requests
let inFlightPublicPromise = null;
let inFlightSettingsKey = null;
let inFlightSettingsPromise = null;

export const branding = {
  state: {
    ...initialBranding,
  },
  reducers: {
    setBranding(state, payload) {
      const merged = {
        ...state,
        ...payload,
        isCustomLoaded: true,
        isLoading: false,
        error: null,
      };
      updateBrandingDom(merged);

      // Persist to localStorage cache
      if (payload && typeof window !== 'undefined') {
        try {
          const authData = JSON.parse(
            getCookie('adminAuth') || localStorage.getItem('adminAuth') || '{}'
          );
          const tenantId = authData?.tenantId;
          const hasToken = document.cookie.indexOf('adminToken=') !== -1;

          if (hasToken && tenantId && tenantId !== 'null' && tenantId !== 'undefined') {
            localStorage.setItem(
              `app_tenant_branding_${tenantId}`,
              JSON.stringify(merged)
            );
            localStorage.setItem(
              'app_active_tenant_branding',
              JSON.stringify(merged)
            );
          } else {
            localStorage.setItem('app_public_branding', JSON.stringify(merged));
          }
        } catch {}
      }
      return merged;
    },

    /**
     * Prepares state for switching to a new tenant.
     * Prevents previous tenant branding or default green theme from appearing.
     */
    prepareTenantSwitch(state, targetTenantId) {
      if (targetTenantId && typeof window !== 'undefined') {
        try {
          const cached = localStorage.getItem(`app_tenant_branding_${targetTenantId}`);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed && (parsed.primaryColor || parsed.logoUrl)) {
              updateBrandingDom(parsed);
              return {
                ...parsed,
                isCustomLoaded: true,
                isLoading: false,
                error: null,
              };
            }
          }
        } catch {}
      }

      // If target tenant is not yet cached, wipe previous assets to prevent bleed
      const cleared = {
        ...state,
        primaryColor: '',
        secondaryColor: '',
        accentColor: '',
        logo: null,
        logoUrl: null,
        logoBlue: null,
        isCustomLoaded: false,
        isLoading: true,
        error: null,
      };
      return cleared;
    },

    resetBranding() {
      const defaults = getDefaultBranding();
      const resetState = {
        ...defaults,
        primaryColor: '',
        secondaryColor: '',
        accentColor: '',
        logo: null,
        logoUrl: null,
        logoBlue: null,
        isCustomLoaded: false,
        isLoading: false,
        error: null,
      };
      updateBrandingDom(resetState);
      return resetState;
    },

    setLoading(state, isLoading) {
      return { ...state, isLoading };
    },

    setError(state, error) {
      return { ...state, error, isLoading: false };
    },
  },

  effects: (dispatch) => ({
    /**
     * 1. Public Branding API – Login Page
     * Endpoint: GET /api/v1/public/branding
     * Called before user authentication to display public product branding.
     */
    async loadPublicBrandingAsync(tenantId = null) {
      const cacheKey = `public_${tenantId || 'default'}`;
      if (inFlightPublicPromise && inFlightSettingsKey === cacheKey) {
        return inFlightPublicPromise;
      }

      inFlightSettingsKey = cacheKey;
      inFlightPublicPromise = (async () => {
        dispatch.branding.setLoading(true);
        try {
          const defaults = getDefaultBranding();
          const response = await SettingsService.getPublicBranding(tenantId);
          const data = response?.data || response;

          if (data) {
            const primaryColor = data.primaryColor || defaults.primaryColor || '';
            const secondaryColor =
              data.secondaryColor ||
              (primaryColor ? adjustColorBrightness(primaryColor, -15) : '');
            const accentColor =
              data.accentColor ||
              (primaryColor ? adjustColorBrightness(primaryColor, 35) : '');
            const backgroundColor = data.backgroundColor || defaults.backgroundColor || '#ffffff';
            const textColor = data.textColor || defaults.textColor || '#101828';

            const logo = data.logoUrl || defaults.logo || null;
            const logoUrl = data.logoUrl || defaults.logoUrl || null;
            const logoBlue = data.logoUrl || defaults.logoBlue || null;
            const favicon = data.faviconUrl || defaults.favicon || null;
            const faviconUrl = data.faviconUrl || defaults.faviconUrl || null;

            const appName = data.appName || defaults.appName || 'PMS GO';
            const prefix = defaults.prefix;
            const suffix = defaults.suffix;

            const rgb = primaryColor ? hexToRgb(primaryColor) : null;
            const primaryColorRgb = rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : '';

            const publicBranding = {
              appName,
              companyName: data.companyName || defaults.companyName || appName,
              hotelName: data.name || data.hotelName || data.appName || '',
              prefix,
              suffix,
              productLogo: defaults.productLogo,
              hotelLogo: data.logoUrl || null,
              hotelLogoUrl: data.logoUrl || null,
              tagline: data.tagline || defaults.tagline || '',
              logo,
              logoUrl,
              logoBlue,
              favicon,
              faviconUrl,
              primaryColor,
              secondaryColor,
              accentColor,
              backgroundColor,
              textColor,
              primaryColorRgb,
              website: data.website || '',
              phone: data.phone || '',
              email: data.email || '',
              address: data.address || '',
              city: data.city || '',
              state: data.state || '',
              country: data.country || '',
              postalCode: data.postalCode || '',
              currency: data.currency || '',
              timezone: data.timezone || '',
              portalTitle: `${appName} | Login - Property Management System`,
              portalDesc: `Sign in to ${appName} Property Management System.`,
              pageTitleSuffix: `${appName} | Property Management System`,
              showcaseAlt: `${appName} Dashboard Showcase`,
              logoAlt: appName,
            };

            try {
              localStorage.setItem('app_public_branding', JSON.stringify(publicBranding));
            } catch {}

            dispatch.branding.setBranding(publicBranding);
            return publicBranding;
          }
        } catch (err) {
          console.warn('[Branding] GET /api/v1/public/branding failed:', err?.message || err);
          dispatch.branding.setError(err.message);
        } finally {
          dispatch.branding.setLoading(false);
          inFlightPublicPromise = null;
        }
      })();

      return inFlightPublicPromise;
    },

    /**
     * 2. Authenticated Branding API – After Login
     * Endpoint: GET /api/v1/settings/brand (with fallback to /api/v1/settings/branding)
     * Called after authentication with the user's validated tenant context.
     */
    async loadAuthenticatedBrandingAsync(tenantId, rootState) {
      let activeTenantId = tenantId;

      if (!activeTenantId || activeTenantId === 'null' || activeTenantId === 'undefined') {
        activeTenantId = rootState?.auth?.user?.tenantId;
      }
      if (!activeTenantId || activeTenantId === 'null' || activeTenantId === 'undefined') {
        try {
          const authData = JSON.parse(
            getCookie('adminAuth') || localStorage.getItem('adminAuth') || '{}'
          );
          activeTenantId = authData?.tenantId;
        } catch {
          activeTenantId = null;
        }
      }

      if (!activeTenantId || activeTenantId === 'null' || activeTenantId === 'undefined') {
        dispatch.branding.resetBranding();
        return;
      }

      const requestKey = `settings_${activeTenantId}`;
      if (inFlightSettingsPromise && inFlightSettingsKey === requestKey) {
        return inFlightSettingsPromise;
      }

      inFlightSettingsKey = requestKey;
      inFlightSettingsPromise = (async () => {
        dispatch.branding.setLoading(true);

        try {
          const defaults = getDefaultBranding();
          let fetchedData = null;

          // 1. Primary data source: GET /api/v1/settings/brand
          try {
            const brandingResponse = await SettingsService.getBranding(activeTenantId);
            if (brandingResponse) {
              fetchedData = brandingResponse.data || brandingResponse;
            }
          } catch (err) {
            console.warn('[Branding] SettingsService.getBranding fallback to public:', err?.message || err);
            // 2. Fallback to GET /api/v1/public/branding with x-tenant-id
            try {
              const publicResponse = await SettingsService.getPublicBranding(activeTenantId);
              if (publicResponse) {
                fetchedData = publicResponse.data || publicResponse;
              }
            } catch (publicErr) {
              console.warn('[Branding] SettingsService.getPublicBranding fallback:', publicErr?.message || publicErr);
            }
          }

          // 3. Fallback to Hotel profile if settings branding is not populated
          if (!fetchedData || (!fetchedData.primaryColor && !fetchedData.logoUrl)) {
            try {
              const tenants = rootState?.auth?.tenants || [];
              const activeTenant = tenants.find(
                (t) => String(t.tenantId || t.id || t._id) === String(activeTenantId)
              );
              const hotelId = activeTenant?.id || activeTenant?._id || rootState?.auth?.user?.hotelId;

              if (hotelId) {
                const hotelResponse = await HotelService.getById(hotelId, activeTenantId);
                const hotelData = hotelResponse?.data || hotelResponse;
                if (hotelData) {
                  fetchedData = { ...hotelData, ...(fetchedData || {}) };
                }
              } else if (activeTenant) {
                fetchedData = { ...activeTenant, ...(fetchedData || {}) };
              }
            } catch (hotelErr) {
              console.warn('[Branding] HotelService fallback:', hotelErr?.message || hotelErr);
            }
          }

          if (!fetchedData) {
            dispatch.branding.resetBranding();
            return;
          }

          // Platform brand suite identity (PMS GO in Dev | Stekay in Prod)
          const appName = defaults.appName;
          const companyName = defaults.companyName;
          const prefix = defaults.prefix;
          const suffix = defaults.suffix;
          const logo = fetchedData.logoUrl || defaults.logo || null;
          const logoUrl = fetchedData.logoUrl || defaults.logoUrl || null;
          const logoBlue = fetchedData.logoUrl || defaults.logoBlue || null;
          const favicon = fetchedData.faviconUrl || defaults.favicon || null;
          const faviconUrl = fetchedData.faviconUrl || defaults.faviconUrl || null;

          // Tenant / Hotel specific information
          const hotelName = fetchedData.name || fetchedData.hotelName || fetchedData.appName || '';

          // Theme colors
          const primaryColor =
            fetchedData.primaryColor ||
            fetchedData.themeColor ||
            defaults.primaryColor ||
            '';

          const secondaryColor =
            fetchedData.secondaryColor ||
            (primaryColor ? adjustColorBrightness(primaryColor, -15) : '');

          const accentColor =
            fetchedData.accentColor ||
            (primaryColor ? adjustColorBrightness(primaryColor, 35) : '');

          const backgroundColor =
            fetchedData.backgroundColor ||
            fetchedData.bgColor ||
            defaults.backgroundColor ||
            '#ffffff';

          const textColor =
            fetchedData.textColor ||
            fetchedData.fontColor ||
            defaults.textColor ||
            '#101828';

          const rgb = primaryColor ? hexToRgb(primaryColor) : null;
          const primaryColorRgb = rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : '';

          const dynamicBranding = {
            appName,
            companyName,
            hotelName,
            prefix,
            suffix,
            productLogo: defaults.productLogo,
            hotelLogo: fetchedData.logoUrl || null,
            hotelLogoUrl: fetchedData.logoUrl || null,
            tagline: fetchedData.tagline || defaults.tagline || '',
            logo,
            logoUrl,
            logoBlue,
            favicon,
            faviconUrl,
            primaryColor,
            secondaryColor,
            accentColor,
            backgroundColor,
            textColor,
            primaryColorRgb,
            website: fetchedData.website || '',
            phone: fetchedData.phone || '',
            email: fetchedData.email || '',
            address: fetchedData.address || '',
            city: fetchedData.city || '',
            state: fetchedData.state || '',
            country: fetchedData.country || '',
            postalCode: fetchedData.postalCode || '',
            currency: fetchedData.currency || '',
            timezone: fetchedData.timezone || '',
            portalTitle: `${appName} | Login - Property Management System`,
            portalDesc: `Sign in to ${appName} Property Management System.`,
            pageTitleSuffix: `${appName} | Property Management System`,
            showcaseAlt: `${appName} Dashboard Showcase`,
            logoAlt: appName,
          };

          try {
            localStorage.setItem(
              `app_tenant_branding_${activeTenantId}`,
              JSON.stringify(dynamicBranding)
            );
            localStorage.setItem(
              'app_active_tenant_branding',
              JSON.stringify(dynamicBranding)
            );
          } catch {}

          dispatch.branding.setBranding(dynamicBranding);
          return dynamicBranding;
        } catch (error) {
          console.error('[Branding] Error in loadAuthenticatedBrandingAsync:', error);
          dispatch.branding.setError(error.message);
        } finally {
          dispatch.branding.setLoading(false);
          inFlightSettingsPromise = null;
        }
      })();

      return inFlightSettingsPromise;
    },

    /**
     * Backward-compatible alias for existing call sites
     */
    async loadBrandingAsync(tenantId, rootState) {
      return dispatch.branding.loadAuthenticatedBrandingAsync(tenantId, rootState);
    },
  }),
};
